import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Box, Typography, IconButton, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomTextField from "../../commons/CustomTextField";
import { API } from "../../api/api";
import { useDispatch } from "react-redux";
import * as patientActions from "../../redux/actions/patientActions";
import { formatWeeks } from "../../utils/formatWeekAndDays";
import moment from "moment";
import { renderFieldControl } from "./TestBlocks";
import ReferenceRange from "./ReferenceRange";

const TestApprovalAccordionItem = ({ test, patientId, expanded, onChange, patientType, onTestApproved }) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [formValues, setFormValues] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (expanded) {
            // Initialize form values 
            const extracted = test?.values || [];
            if (typeof extracted === 'object' && !Array.isArray(extracted)) {
                const arr = Object.keys(extracted).map(key => ({ fieldKey: key, value: extracted[key] }));
                setFormValues(arr);
            } else {
                setFormValues(extracted);
            }

            if (test.fileMediaId && !previewUrl) {
                setLoadingMedia(true);
                const fetchMedia = async () => {
                    try {
                        let baseUrl =
                            process.env.NODE_ENV === 'development'
                                ? process.env.REACT_APP_DEVELOP
                                : process.env.REACT_APP_PROD;
                        const token = localStorage.getItem("token");
                        const res = await fetch(`${baseUrl}/v1/media/${test.fileMediaId}`, {
                            method: "GET",
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        const fileUrl = res?.url;
                        if (fileUrl) {
                            setPreviewUrl(fileUrl);
                            if (fileUrl.includes(".pdf")) {
                                setPreviewType("pdf");
                            } else {
                                setPreviewType("image");
                            }
                        }
                    } catch (err) {
                        console.error("View error:", err);
                    } finally {
                        setLoadingMedia(false);
                    }
                };
                fetchMedia();
            }
        }
    }, [expanded, test]);

    const handleValueChange = (fieldKey, newValue) => {
        setFormValues(prev => {
            const existing = prev.find(v => v.fieldKey === fieldKey);
            if (existing) {
                return prev.map(v => v.fieldKey === fieldKey ? { ...v, value: newValue } : v);
            } else {
                return [...prev, { fieldKey, value: newValue }];
            }
        });
    };

    const handleUpdate = async () => {
        setIsUpdating(true);
        try {
            await API.patch(`/v1/tests/${test.id}`, { values: formValues });
            dispatch(patientActions.getPatientTestsRequestAction(patientId));
        } catch (err) {
            console.error("Failed to update test values", err);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            await API.post(`/v1/tests/${test.id}/approve`);
            dispatch(patientActions.getPatientTestsRequestAction(patientId));
            onTestApproved(test.id);
        } catch (err) {
            console.error("Failed to approve test", err);
        } finally {
            setIsApproving(false);
        }
    };

    const getTenantFieldKeys = (fields) => {
        let keys = new Set();
        const traverse = (flds) => {
            if (!flds) return;
            flds.forEach(f => {
                if (f.fieldKey) keys.add(f.fieldKey);
                if (f.children) traverse(f.children);
            });
        };
        traverse(fields);
        return keys;
    };
    
    const tenantFieldKeys = getTenantFieldKeys(test.tenantTest?.fields);
    const missingFields = formValues.filter(v => v.fieldKey && !tenantFieldKeys.has(v.fieldKey));

    return (
        <Accordion expanded={expanded} onChange={onChange} sx={{ mb: 1, border: '1px solid #E6E8EC', boxShadow: 'none', '&:before': { display: 'none' }, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: expanded ? '#F8F9FA' : '#FFF', borderBottom: expanded ? '1px solid #E6E8EC' : 'none', borderRadius: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>{test.tenantTest?.testName || test.name || "Test"}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {test.labName && <Typography sx={{ fontSize: '12px', color: '#666' }}>{test.labName}</Typography>}
                        <Typography sx={{ fontSize: '12px', color: '#666' }}>{patientType !== "GYNECOLOGY" ? formatWeeks(test.week) : moment(test.updatedAt).format('DD-MM-YYYY')}</Typography>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, p: 3, backgroundColor: '#FAFAFA', gap: 3 }}>
                
                {/* PDF PREVIEW (LEFT) */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, mb: { xs: 3, md: 0 }, height: { xs: '300px', md: '500px' }, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}>Document Preview</Typography>
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6E8EC', borderRadius: 2, backgroundColor: '#FFF' }}>
                        {loadingMedia ? <CircularProgress /> : previewUrl ? (
                            previewType === "image" ? (
                                <img src={previewUrl} alt="preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: 'contain' }} />
                            ) : (
                                <iframe src={previewUrl} title="pdf-preview" width="100%" height="100%" style={{ border: "none" }} />
                            )
                        ) : (
                            <Typography sx={{ color: '#999' }}>No document available</Typography>
                        )}
                    </Box>
                </Box>

                {/* EXTRACTED VALUES (RIGHT) */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, display: 'flex', flexDirection: 'column', border: '1px solid #E6E8EC', borderRadius: 2, backgroundColor: '#FFF', p: 3 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mb: 2 }}>Review Extracted Values</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, overflowY: 'auto', maxHeight: { xs: 'none', md: '360px' }, pr: 1 }}>
                        {(test.tenantTest?.fields || []).map((field, idx) => {
                            if (field.fieldType === 'OBJECT') {
                                return (
                                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0, color: '#333' }}>
                                            {field.fieldName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2, borderLeft: '2px solid #E6E8EC' }}>
                                            {(field.children || []).map((child, childIdx) => {
                                                const currentValue = formValues.find(v => v.fieldKey === child.fieldKey)?.value || '';
                                                return (
                                                    <Box key={childIdx} sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <ReferenceRange printed={test?.referenceRange?.[child.fieldKey]} template={child.referenceRange}>
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, mb: 0 }}>
                                                                {child.fieldName} {child.unit ? `(${child.unit})` : ''}
                                                            </Typography>
                                                        </ReferenceRange>
                                                        <CustomTextField
                                                            value={currentValue}
                                                            setValue={(val) => handleValueChange(child.fieldKey, val)}
                                                            edit={true}
                                                            width="100%"
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            } else {
                                const currentValue = formValues.find(v => v.fieldKey === field.fieldKey)?.value || '';
                                return (
                                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                                        <ReferenceRange printed={test?.referenceRange?.[field.fieldKey]} template={field.referenceRange}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, mb: 0 }}>
                                                {field.fieldName} {field.unit ? `(${field.unit})` : ''}
                                            </Typography>
                                        </ReferenceRange>
                                        {renderFieldControl({
                                            val: field,
                                            fieldValue: currentValue,
                                            edit: true,
                                            onValueChange: (val) => handleValueChange(field.fieldKey, val)
                                        })}

                                        {/* Nested rows / child fields (for non-OBJECT fields) */}
                                        {(field.children || []).map((nestedChild, nestedIdx) => {
                                            const hasSubChildren = (nestedChild?.children || []).length > 0;
                                            return (
                                                <Box key={`nested-${nestedIdx}`} sx={{ pl: 2, mt: 0, display: 'flex', flexDirection: 'column', borderLeft: '2px solid #E6E8EC' }}>
                                                    {nestedChild.fieldName && hasSubChildren && (
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 'bold', mb: 0, color: '#444' }}>
                                                            {nestedChild.fieldName}
                                                        </Typography>
                                                    )}
                                                    {hasSubChildren ? (
                                                        nestedChild.children.map((innerChild, innerIdx) => {
                                                            const innerValue = formValues.find(v => v.fieldKey === innerChild.fieldKey)?.value || '';
                                                            return (
                                                                <Box key={`inner-${innerIdx}`} sx={{ display: 'flex', flexDirection: 'column', mt: 0, pl: 1 }}>
                                                                    <ReferenceRange printed={test?.referenceRange?.[innerChild.fieldKey]} template={innerChild.referenceRange}>
                                                                        <Typography sx={{ fontSize: '12px', fontWeight: 500, mb: 0 }}>
                                                                            {innerChild.fieldName} {innerChild.unit ? `(${innerChild.unit})` : ''}
                                                                        </Typography>
                                                                    </ReferenceRange>
                                                                    {renderFieldControl({
                                                                        val: innerChild,
                                                                        fieldValue: innerValue,
                                                                        edit: (!field?.options?.includes('Positive') ? true : currentValue === 'Positive'),
                                                                        onValueChange: (val) => handleValueChange(innerChild.fieldKey, val)
                                                                    })}
                                                                </Box>
                                                            );
                                                        })
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0 }}>
                                                            <ReferenceRange printed={test?.referenceRange?.[nestedChild.fieldKey]} template={nestedChild.referenceRange}>
                                                                <Typography sx={{ fontSize: '12px', fontWeight: 500, mb: 0 }}>
                                                                    {nestedChild.fieldName} {nestedChild.unit ? `(${nestedChild.unit})` : ''}
                                                                </Typography>
                                                            </ReferenceRange>
                                                            {renderFieldControl({
                                                                val: nestedChild,
                                                                fieldValue: formValues.find(v => v.fieldKey === nestedChild.fieldKey)?.value || '',
                                                                edit: true, // we could apply similar logic here if needed
                                                                onValueChange: (val) => handleValueChange(nestedChild.fieldKey, val)
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            }
                        })}
                        
                        {missingFields.length > 0 && (
                            <Box sx={{ mt: 0.5, pt: 1, borderTop: '1px dashed #E6E8EC' }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0.5, color: '#F27704' }}>
                                    Additional Values
                                </Typography>
                                {missingFields.map((field, idx) => (
                                    <Box key={`missing-${idx}`} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                                        <ReferenceRange printed={field.referenceRange || test?.referenceRange?.[field.fieldKey]}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, mb: 0, textTransform: 'capitalize' }}>
                                                {field.fieldName || field.fieldKey}
                                            </Typography>
                                        </ReferenceRange>
                                        <CustomTextField
                                            value={field.value || ''}
                                            setValue={(val) => handleValueChange(field.fieldKey, val)}
                                            edit={true}
                                            width="100%"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* ACTIONS */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E6E8EC', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleUpdate} 
                            disabled={isUpdating}
                            sx={{ width: '120px', fontSize: '13px', textTransform: 'none', color: '#F27704', borderColor: '#F27704' }}
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleApprove} 
                            disabled={isApproving}
                            sx={{ width: '120px', fontSize: '13px', textTransform: 'none', backgroundColor: '#F27704' }}
                        >
                            {isApproving ? 'Approving...' : 'Approve'}
                        </Button>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const TestApprovalModal = ({ open, onClose, pendingTests = [], patientId, isMobile, patientType }) => {
    const [expandedTestId, setExpandedTestId] = useState(null);

    useEffect(() => {
        if (open && pendingTests.length > 0 && !expandedTestId) {
            setExpandedTestId(pendingTests[0].id);
        }
    }, [open, pendingTests]);

    const handleAccordionChange = (testId) => (event, isExpanded) => {
        setExpandedTestId(isExpanded ? testId : false);
    };

    const handleTestApproved = (testId) => {
        // Auto-expand the next test in the list
        const nextTest = pendingTests.find(t => t.id !== testId);
        if (nextTest) {
            setExpandedTestId(nextTest.id);
        } else {
            // Close modal if no more pending tests
            onClose();
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
            <DialogContent sx={{ position: "relative", p: 3, display: 'flex', flexDirection: 'column', height: '85vh', backgroundColor: '#F8F9FA' }}>
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 10, top: 10, zIndex: 10, backgroundColor: '#FFF', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <CloseIcon />
                </IconButton>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Tests Pending Approval ({pendingTests.length})
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '13px' }}>
                        Please review the extracted documents and approve the values below.
                    </Typography>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                    {pendingTests.length > 0 ? (
                        pendingTests.map(test => (
                            <TestApprovalAccordionItem 
                                key={test.id} 
                                test={test} 
                                patientId={patientId}
                                patientType={patientType}
                                expanded={expandedTestId === test.id}
                                onChange={handleAccordionChange(test.id)}
                                onTestApproved={handleTestApproved}
                            />
                        ))
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography sx={{ color: '#999' }}>All tests have been approved.</Typography>
                        </Box>
                    )}
                </Box>

            </DialogContent>
        </Dialog>
    );
};

export default TestApprovalModal;
